import React, { useContext, useEffect, useRef, useState } from 'react';
import { apiRequestBlob } from '@/lib/api/client';
import { I18nContext } from '@/i18n/I18nProvider';
import { doctoralApi } from '@/features/workspace/api';

type Mode = 'instruction' | 'samples' | 'fill';
type Locale = 'kz' | 'ru' | 'en';

const MODE_LABEL: Record<Mode, Record<Locale, string>> = {
  instruction: { kz: 'Нұсқаулық', ru: 'Инструкция', en: 'Instructions' },
  samples: { kz: 'Үлгілер', ru: 'Образцы', en: 'Templates' },
  fill: { kz: 'Толтыру', ru: 'Заполнение', en: 'Complete the form' },
};

const INSTRUCTIONS: Record<string, string[]> = {
  topic: [
    'Раздел нужен, чтобы утвердить тему и научных консультантов одним комплектом, а не отдельными анкетами.',
    'Участвуют докторант, научный руководитель, зарубежный консультант и эдвайзер, который проверяет комплект.',
    'Порядок: заявление → обоснование → письмо-согласие → техническая спецификация → сведения о консультанте → представление.',
    'Тема хранится отдельно на казахском, русском и английском. Пустую языковую версию система не подставляет сама.',
  ],
  plan: [
    'Индивидуальный план связывает дисциплины, НИРД, практики, стажировку и подготовку к защите.',
    'Докторант загружает свой файл. Эдвайзер проверяет эту версию, а не общий образец.',
  ],
  nird: [
    'НИРД заполняется по применимым семестрам. Отчёт семестра — личный документ докторанта.',
    'Чтение инструкции и скачивание образца не считаются сдачей отчёта.',
  ],
  practices: [
    'Педагогическая и исследовательская практики ведутся по своим документам: план-график, дневник-отчёт, приказ.',
    'Образец и сданный файл — разные документы.',
  ],
  internship: [
    'Научная стажировка: план, приглашение, приказ, отчёт и сертификат по фактическим датам.',
    'Даты берутся из внесённых сведений, а не из демонстрационного календаря.',
  ],
  publications: [
    'Каждая публикация — отдельная запись с выходными данными. Образец карточки не создаёт публикацию.',
  ],
  achievements: [
    'Достижения и сертификаты добавляются по факту. Пустой перечень не показывается как заполненный.',
  ],
  defense: [
    'Предзащита и защита идут по этапам. На проверку уходит подготовленный комплект, а не текст инструкции.',
  ],
  completion: [
    'Завершение обучения собирает архив, отработку и диплом по фактическим документам докторанта.',
  ],
};

const LOCAL_SAMPLES: Record<string, [string, string, string][]> = {
  topic: [
    ['application', '1-қосымша · Өтініш', 'Приложение 1 — заявление.'],
    ['justification', '2-қосымша · Негіздеме', 'Приложение 2 — обоснование.'],
    ['consent', '3-қосымша · Келісімхат', 'Приложение 3 — письмо-согласие.'],
    ['tech_spec', '4-қосымша · Техникалық спецификация', 'Приложение 4 — техническая спецификация.'],
    ['consultant', '5-қосымша · Ғылыми кеңесші туралы мәлімет', 'Приложение 5 — сведения о научном консультанте.'],
    ['proposal', '6-қосымша · Ұсыныс', 'Приложение 6 — представление / предложение.'],
  ],
  plan: [['iup', 'Индивидуальный план', 'Рабочий образец ИУП текущей редакции.']],
  nird: [['report', 'Отчёт НИРД', 'Образец семестрового отчёта.']],
  practices: [
    ['schedule', 'План-график практики', 'Образец план-графика.'],
    ['diary', 'Дневник-отчёт практики', 'Образец дневника-отчёта.'],
  ],
  internship: [
    ['plan', 'План научной стажировки', 'Образец плана стажировки.'],
    ['invitation', 'Приглашение', 'Образец приглашения.'],
  ],
  publications: [['card', 'Карточка публикации', 'Образец описания статьи.']],
  achievements: [['list', 'Перечень достижений', 'Образец перечня.']],
  defense: [['pack', 'Пакет предзащиты', 'Состав пакета по действующей редакции.']],
  completion: [['archive', 'Выпускное дело', 'Перечень документов архивного дела.']],
};

interface SampleRow {
  code: string;
  title: string;
  description: string;
  available: boolean;
}

interface SectionState {
  review_status?: string;
  status_label?: string;
  advisor_comment?: string | null;
  comment_target?: string | null;
  last_save_kind?: string | null;
  filled_count?: number;
  required_count?: number;
  confirmation_cleared?: boolean;
  samples?: SampleRow[];
}

interface DoctoralSectionModesProps {
  section: string;
  children: React.ReactNode;
  onSave?: (kind: 'manual' | 'autosave') => Promise<void>;
  autosaveSignal?: string;
}

const SAVE_KIND_LABEL: Record<string, string> = {
  autosave: 'Автосохранение',
  manual: 'Сохранено вручную',
  submit: 'Отправлено на проверку',
};

async function openFile(path: string, disposition: 'inline' | 'attachment') {
  const result = await apiRequestBlob(path, { channel: 'platform', query: { disposition } });
  const url = URL.createObjectURL(result.blob);
  window.open(url, '_blank', 'noopener');
}

export const DoctoralSectionModes: React.FC<DoctoralSectionModesProps> = ({
  section,
  children,
  onSave,
  autosaveSignal,
}) => {
  const i18n = useContext(I18nContext);
  const locale: Locale = i18n?.locale === 'kz' || i18n?.locale === 'en' ? i18n.locale : 'ru';
  const [mode, setMode] = useState<Mode>('fill');
  const [state, setState] = useState<SectionState | null>(null);
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);

  const reload = () => {
    const request = doctoralApi.sectionState?.(section);
    if (!request) return;
    request.then(setState).catch(() => setState(null));
  };

  useEffect(() => {
    reload();
  }, [section]);

  const skipAutosave = useRef(true);

  useEffect(() => {
    skipAutosave.current = true;
  }, [section]);

  useEffect(() => {
    if (!onSave || autosaveSignal === undefined) return;
    if (skipAutosave.current) {
      skipAutosave.current = false;
      return;
    }
    const timer = window.setTimeout(() => {
      onSave('autosave')
        .then(() => {
          setNotice('');
          reload();
        })
        .catch(() => setNotice('Не удалось автосохранить. Данные остались на экране, повторите сохранение.'));
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [autosaveSignal]);

  const samples: SampleRow[] = (state?.samples?.length ? state.samples : (LOCAL_SAMPLES[section] || []).map(
    ([code, title, description]) => ({ code, title, description, available: false }),
  ));

  const persist = async (kind: 'manual' | 'autosave' | 'submit') => {
    setSaving(true);
    setNotice('');
    try {
      if (onSave) await onSave(kind === 'submit' ? 'manual' : kind);
      if (kind === 'submit' || !onSave) await doctoralApi.saveSection?.(section, kind);
      setNotice('');
      reload();
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      setNotice(message || 'Не удалось сохранить. Проверьте соединение и повторите.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="dp120-subtabs" role="tablist" aria-label="Режимы раздела">
        {(['instruction', 'samples', 'fill'] as Mode[]).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            className={mode === key ? 'active gold' : ''}
            aria-selected={mode === key}
            onClick={() => setMode(key)}
          >
            {MODE_LABEL[key][locale]}
          </button>
        ))}
      </div>
      {state ? (
        <div className="dp120-callout" style={{ marginBottom: 10 }}>
          <b>{state.status_label || 'Не начато'}</b>
          {' · '}
          заполнено {state.filled_count ?? 0} из {state.required_count ?? 0}
          {state.last_save_kind ? ` · ${SAVE_KIND_LABEL[state.last_save_kind] || state.last_save_kind}` : ''}
        </div>
      ) : null}
      {state?.confirmation_cleared ? (
        <div className="dp120-callout" style={{ marginBottom: 10 }}>
          <b>Прежнее подтверждение снято.</b> Изменённый материал нужно отправить на проверку снова.
        </div>
      ) : null}
      {state?.advisor_comment ? (
        <div className="dp120-callout" style={{ marginBottom: 10 }}>
          <b>Замечание{state.comment_target ? ` · ${state.comment_target}` : ''}</b>
          <div>{state.advisor_comment}</div>
        </div>
      ) : null}
      {notice ? (
        <div className="dp120-callout" style={{ marginBottom: 10 }}>
          <b>{notice}</b>
        </div>
      ) : null}

      {mode === 'instruction' && (
        <div className="dp120-grid">
          {(INSTRUCTIONS[section] || []).map((line) => (
            <article key={line} className="dp120-card span12">
              <div className="dp120-card-body">
                <p>{line}</p>
              </div>
            </article>
          ))}
        </div>
      )}

      {mode === 'samples' && (
        <div className="dp120-list">
          {samples.map((sample) => (
            <div key={sample.code} className="dp120-doc">
              <div>
                <h4>{sample.title}</h4>
                <p>{sample.description}</p>
                {!sample.available ? (
                  <span className="dp120-badge warn">Файл текущей редакции ещё не в библиотеке</span>
                ) : null}
              </div>
              <div className="dp120-row-actions">
                <button
                  type="button"
                  className="dp120-mini"
                  disabled={!sample.available}
                  onClick={() =>
                    openFile(`/doctoral/process/sections/${section}/samples/${sample.code}`, 'inline').catch(() =>
                      setNotice('Не удалось открыть образец.'),
                    )
                  }
                >
                  Открыть
                </button>
                <button
                  type="button"
                  className="dp120-mini gold"
                  disabled={!sample.available}
                  onClick={() =>
                    openFile(`/doctoral/process/sections/${section}/samples/${sample.code}`, 'attachment').catch(() =>
                      setNotice('Не удалось скачать образец.'),
                    )
                  }
                >
                  Скачать
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {mode === 'fill' && (
        <>
          <div className="dp120-hero-actions" style={{ marginBottom: 10 }}>
            <button type="button" className="dp120-btn gold" disabled={saving} onClick={() => persist('manual')}>
              Сохранить
            </button>
            <button type="button" className="dp120-btn mint" disabled={saving} onClick={() => persist('submit')}>
              Отправить на проверку
            </button>
          </div>
          {children}
        </>
      )}
    </>
  );
};
